import { FC, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Id, toast } from "react-toastify";
import { DialogContent } from "../../components/dialog/Dialog";
import { useDialog } from "../../components/dialog/DialogContext";
import { Direction } from "../../constants/Direction";
import { FILTER_CURRENT, FILTER_DATE, FILTER_TIME, FILTER_INACTIVE_FOR, FILTER_TIMEOUT_MINUTES, FILTER_RESULT } from "../../constants/filter-types";
import { exportReport } from "../../exporters/ExportHelper";
import { ButtonTypes } from "../../models/ButtonTypes";
import { CellWarning, CustomDeleteKey, CustomRowDeleteMsgTrnKeyWithCondition, CustomRowModifier, CustomRowModifierWithRange } from "../../models/CustomRowAction";
import { ExportableReport } from "../../models/ExportableReport";
import { Filter } from "../../models/Filter";
import { ListData } from "../../models/ListData";
import { ListQuery } from "../../models/ListQuery";
import { Lookup } from "../../models/Lookup";
import { MultiSelectOption } from "../../models/MultiSelectOption";
import { PaginationModel } from "../../models/Pagination";
import { SortBy } from "../../models/SortBy";
import { UserPreferenceFilter } from "../../models/UserPreferenceFilter";
import { SearchTerm } from "../../models/search/SearchTerm";
import { DeleteEntityResult } from "../../models/services/DeleteEntityResult";
import { RowTooltipConfig } from "../../models/table/RowTooltipConfig";
import { TableColumn } from "../../models/table/TableColumn";
import { TableLegendItem } from "../../models/table/TableLegendItem";
import { deleteSettingData } from "../../services/settingDataService";
import { deleteUserPreference, getSettingDataForUserLogin, getUserColumnsByView, getUserFilterPreference, storeSettingData, storeUserPreferenceColumn, storeUserPreferenceFilters } from "../../services/userPreferenceService";
import { getErrorMessage, showError } from "../../services/utilities/ServiceError";
import { IAppState } from "../../store/types";
import { getDateWithFullTime, processSelectedDates, processSelectedInactivePeriods } from "../../utilities/DateUtils";
import { isClearLocked, shouldRestoreFilterOnClear } from "../../utilities/filterClearPolicy";
import { TranslationKeys } from "../TranslationKeys";
import { useLocale } from "../localeContext";
import { FilterContext } from "./FilterContext";
import { LegendContext } from "./LegendContext";
import { PaginationContext, emptyPaginationContextValue } from "./PaginationContext";
import { SearchContext } from "./SearchContext";
import { SortContext } from "./SortContext";
import { TableContext } from "./TableContext";
import { performCheck } from "@/utilities/genericUtil";

interface FilterPreference {
    filterId;
    preferenceId;
}

interface UserPreferenceKey {
    userLoginId;
    currentMenuDetailId;
    reportId;
    settingId;
}

export interface TableContextProviderValueType {
    children;
    tableDescriptor?: TableColumn[];
    searchDescriptor?: SearchTerm[];
    dataMethod?: (query?: ListQuery) => Promise<ListData>;
    legendItems?: TableLegendItem[];
    initialiser?: (setDetails: (userLoginId, currentMenuDetail, customFilters?) => void) => void;
    useTable?: boolean;
    isMultiSelect?: boolean;
    usePagination?: boolean;
    useFilter?: boolean;
    useSearch?: boolean;
    useLegend?: boolean;
    useSort?: boolean;
    legendKey?: string;
    legendLabel?: string;
    /**
     * This function is mainly used if you want to refresh the full data by calling a new data method
     * that was supplied.  
     *
     * i.e. For views that populate that using websockets and you want to refresh the data on demand.
     *
     * @param manualDataRefresh this will pull new data using the given data method
     * @returns 
     */
    manualDataRefresh?: (manualDataRefresh: (newDataMethod?: (query?: ListQuery) => Promise<ListData>) => void) => void;
    deleteMethod?: (id: string, isCustomDeleteKey?: boolean) => Promise<DeleteEntityResult>;
    editMethod?: (id: string) => void;
    /**
     * This function is close manualDataRefresh, the difference is that this function updates the exisiting table data
     * whereas manualDataRefresh gets new data from the dataMethod 
     *
     * @param tableDataUpdate this will be the current dataset shown in the table
     * @returns 
     */
    tableDataUpdate?: (tableDataUpdate: (updateTableMethod: (tableData: any[]) => any[]) => void) => void;
    swapMethod?: (item: any) => void;
    maxPageSize?: number;
    disableSearch?: boolean;
    customDeleteKey?: CustomDeleteKey;
    alwaysSelectedAndDisabled?: string[];
    downloadMethod?: (item: any) => void;
    lookup?: Lookup[];
    customDelete?: CustomRowModifier,
    customDeleteWithRange?: CustomRowModifierWithRange,
    /**
     * This is a generic translation key that will be used in all the rows for the table.
     */
    customDeleteMsgTrnKey?: string,
    /**
     * This is a specific translation key that will only apply to rows that meet the condition.
     */
    customDeleteMsgTrnKeyWithCondition?: CustomRowDeleteMsgTrnKeyWithCondition,
    customEditWithRange?: CustomRowModifierWithRange,
    customEdit?: CustomRowModifier,
    customDownload?: CustomRowModifier,
    canExportReport?: boolean,
    childColumns?: TableColumn[];
    manuallyMuster?: (rowData: any) => void;
    customMusteringWithRange?: CustomRowModifierWithRange;
    getOriginalStaticFilter?: (filterKey: string) => Promise<Filter>;
    onMoveUpOrDown?: (item:any, rowIndex: number, direction: 'up' | 'down') => void;
    /**
     * This is useful for view that have a lot of data and where it is not ideal to pull all the data at once, 
     * in this case you can set loadDataOnDemand to true and the table will not pull data on startup but will
     * wait until you explicitly request it.
     */
    loadDataOnDemand?: boolean;
    emptyStateMessage?: string;
    onAddNew?: () => void;
    showAddNewButton?: boolean;
    runDataOnDemandButtonLabel?: string;
    rowTooltipConfig?: RowTooltipConfig;
    /**
     * When true, forces the busy/loading spinner on regardless of the internal busy state.
     * Useful for views (e.g. websocket-driven) where data arrives asynchronously outside the
     * normal dataMethod request/response cycle and the internal busy flag would otherwise
     * clear before any data has arrived.
     */
    externalBusy?: boolean;
}

export const TableContextProvider: FC<TableContextProviderValueType> = ({
    children,
    tableDescriptor: newTableDescriptor = new Array(0),
    searchDescriptor: newSearchDescriptor = new Array(0),
    dataMethod: newDataMethod = () => { return new Promise((_, reject) => { reject({ rows: new Array(0), pagination: emptyPaginationContextValue.pagination }) }) },
    legendItems = [],
    initialiser = (setDetails) => { setDetails(undefined, undefined, []) },
    useTable = false,
    isMultiSelect = true,
    usePagination = false,
    useFilter = false,
    useSearch = false,
    useLegend = false,
    useSort = false,
    legendKey = undefined,
    legendLabel = undefined,
    manualDataRefresh = undefined,
    deleteMethod = undefined,
    editMethod = undefined,
    tableDataUpdate = undefined,
    swapMethod = undefined,
    maxPageSize = undefined,
    disableSearch = false,
    customDeleteKey = undefined,
    alwaysSelectedAndDisabled = [],
    downloadMethod = undefined,
    lookup = [],
    customDelete = undefined,
    customDeleteWithRange = undefined,
    customDeleteMsgTrnKey = undefined,
    customDeleteMsgTrnKeyWithCondition =  undefined,
    customEdit = undefined,
    customEditWithRange = undefined,
    customDownload = undefined,
    canExportReport = true,
    childColumns = [],
    manuallyMuster = undefined,
    customMusteringWithRange = undefined,
    getOriginalStaticFilter = undefined,
    onMoveUpOrDown = undefined,
    loadDataOnDemand = false,
    emptyStateMessage = undefined,
    onAddNew = undefined,
    showAddNewButton = true,
    runDataOnDemandButtonLabel = undefined,
    rowTooltipConfig = undefined,
    externalBusy = false
}) => {

    const generatePaginationWithNewMaxSize = () => {
        if (maxPageSize) {
            const newPagination = {
                maxPageSize: maxPageSize,
                pageNumber: 1,
                totalCount: 0,
                totalPages: 1,
                sizes: [maxPageSize, 50, 100],
                pages: [1]
            }

            return newPagination;
        }
        return emptyPaginationContextValue.pagination;
    }

    const [tableData, setTableData] = useState(new Array(0));
    const [groupedTableData, setGroupedTableData] = useState(new Array(0));
    const [dataMethod, setDataMethod] = useState<(query?: ListQuery) => Promise<ListData>>(() => newDataMethod);
    const [pagination, setPagination] = useState<PaginationModel>(generatePaginationWithNewMaxSize());
    const [sort] = useState<SortBy>();
    const [filters, setFilters] = useState<Filter[]>(new Array(0));
    const [searchTerm] = useState<SearchTerm>({ accessor: newSearchDescriptor[0]?.searchOnName ?? '', translationKey: newSearchDescriptor[0]?.translationKey ?? '' });
    const [tableDescriptor, setTableDescriptor] = useState<TableColumn[]>(newTableDescriptor);
    const [searchDescriptor] = useState<TableColumn[]>(newSearchDescriptor);
    const [userPreference, setUserPreferenceColumn] = useState<any>(newSearchDescriptor);
    const initialQuery = {
        filters: new Array(0),
        goToPage: pagination.pageNumber,
        pageSize: maxPageSize ? maxPageSize : pagination.maxPageSize,
        searchTerm: searchTerm,
        sortBy: sort
    };
    const [query] = useState<ListQuery>(initialQuery);
    const [userPreferenceKey, setUserPreferenceKey] = useState<UserPreferenceKey>();
    const { translate } = useLocale();
    const [busy, setBusy] = useState<boolean>(false);
    const user = useSelector((state: IAppState) => (state.userInfo));
    const dialog = useDialog();
    const [userColumnByViewData, setUserColumnByViewData] = useState<any>();
    const exportRef = useRef(true);
    /**
     * This state will be true when execute button is clicked and we load data after loading we need to set it back to false.
     */
    const [requestToLoadDataOnDemand, setRequestToLoadDataOnDemand] = useState<boolean>(false);
    const loadDataToastRef = useRef<Id>(0);
    const filterChangeToastRef = useRef<Id>(0);
    /**
     * Tracks whether the report has been run at least once. Once true, sort/pagination
     * changes bypass the loadDataOnDemand guard and take immediate effect.
     */
    const hasRunOnDemandRef = useRef<boolean>(false);

    useEffect(() => {
        // Clean up function to reset exportRef and dismiss any toasts when the component unmounts
        return () => {
            exportRef.current = false;
            toast.dismiss(loadDataToastRef.current);
            toast.dismiss(filterChangeToastRef.current);
        };
    }, []);

    useEffect(() => {
        getUserColumn();
    }, [userPreferenceKey])

    useEffect(() => {
        if (requestToLoadDataOnDemand) {
            refreshData(query);
            setRequestToLoadDataOnDemand(false);
            toast.dismiss(loadDataToastRef.current);
            loadDataToastRef.current = 0;
            toast.dismiss(filterChangeToastRef.current);
            filterChangeToastRef.current = 0;
        }
    }, [requestToLoadDataOnDemand])

    const showFilterChangeNotification = () => {
        // Only show if loadDataOnDemand is enabled, the report has been run at least once,
        // and no notification is currently displayed.
        if (loadDataOnDemand && hasRunOnDemandRef.current && filterChangeToastRef.current === 0) {
            filterChangeToastRef.current = toast.info(
                translate.byKey(TranslationKeys.filtersHaveChanged),
                {
                    autoClose: false,
                    onClose: () => {
                        filterChangeToastRef.current = 0;
                    }
                }
            );
        }
    };

    const getUserColumn = async () => {
        if (userPreferenceKey?.currentMenuDetailId && userColumnByViewData !== undefined) {
            // No saved column preferences yet — keep all columns visible so we don't
            // incorrectly persist a single isDefault column as the user's preference.
            if (userColumnByViewData.length === 0) {
                //console.log('[TableContextProvider] No column preferences found for user — showing all columns.');
                setUserPreferenceColumn({
                    currentMenuDetail: userPreferenceKey?.currentMenuDetailId,
                    userLoginId: userPreferenceKey?.userLoginId,
                    userPreferenceColumnIds: [],
                    userPreferenceColumms: []
                });
                return;
            }

            let newShownColumns = [] as any;
            try {
                const userPreferenceColumnIds = userColumnByViewData.map((item) => item.columnId)
                tableDescriptor.map((columnId) => {
                    if (!columnId.isFilterOnlyColumn && (userPreferenceColumnIds.includes(columnId.columnId) || columnId.isDefault === true)) {
                        newShownColumns.push({ ...columnId, active: true })
                    } else {
                        newShownColumns.push({ ...columnId, active: false })
                    }
                })
                setUserPreferenceColumn({
                    currentMenuDetail: userPreferenceKey?.currentMenuDetailId,
                    userLoginId: userPreferenceKey?.userLoginId,
                    userPreferenceColumnIds: userColumnByViewData.map((item) => item.columnId),
                    userPreferenceColumms: userColumnByViewData
                });
            } catch (error) {
                showError(error, toast.warn);
            }
            const doesNotHavePreferences = newShownColumns.every((item) => { return item.active === false });
            !doesNotHavePreferences && setTableDescriptor(newShownColumns);
        }
    }

    useEffect(() => {
        handleMenuSelection(tableDescriptor)
    }, [tableDescriptor])

    // fancy callback to inject user and menu details into user preference initialiser method.
    const setDetails = async (userLoginId, currentMenuDetailId, customFilter: Filter[], reportId?: string, staticFilterKeys?: string[], settingId?: string) => {
        try {
            if (userLoginId && currentMenuDetailId) {
                if (customFilter) {
                    filters.push(...customFilter)
                }

                const filterPreferences = await getUserFilterPreference(userLoginId, currentMenuDetailId, reportId, settingId)
                // create a preference-filter mapping facilitating storage and removal on update.
                const newUserFilterPreferences = filterPreferences.map(newFilter => {
                    return { filterId: newFilter.filterId, preferenceId: newFilter.preferenceId };
                })
                const newFilters = filterPreferences.map(newFilter => {
                    // first find the column definition for each filter
                    const columnDescriptor = tableDescriptor.find(descriptor => descriptor.filterId === newFilter.filterId);
                    if (columnDescriptor && columnDescriptor.searchOnName) {
                        const filter: Filter = {
                            key: columnDescriptor.searchOnName,
                            label: translate.byKey(TranslationKeys[columnDescriptor.filterTranslationKey ?? columnDescriptor.translationKey]) ?? columnDescriptor.heading,
                            options: new Array(0),
                            selected: newFilter.filterId === FILTER_DATE ? processSelectedDates(newFilter.selected, translate) // Handle date filter selection
                                : newFilter.filterId === FILTER_INACTIVE_FOR ? processSelectedInactivePeriods(newFilter.selected, translate) // Handle inactive-for filter selection
                                    : newFilter.filterId === FILTER_CURRENT ? mapSavedFilterValuesToLookupLabels(columnDescriptor, newFilter.selected, lookup) // Handle current filter selection
                                        : newFilter.selected // Handle other filter selections
                        }
                        return filter;
                    }
                    return undefined;
                }).filter(filter => filter !== undefined && filter.selected.length !== 0);

                userFilterPreferences.push(...newUserFilterPreferences);
                filters.push(...newFilters as Filter[]);

                const responseUserColumnsByView = await getUserColumnsByView(userLoginId, currentMenuDetailId, reportId, settingId);
                setUserColumnByViewData(responseUserColumnsByView);
                setUserPreferenceKey({ userLoginId, currentMenuDetailId, reportId, settingId });

                const userPreferenceColumnIds = responseUserColumnsByView.map((item) => item.columnId);
                if (userPreferenceColumnIds?.length > 0) {
                    const newTableDescriptor = tableDescriptor.map((descriptor: TableColumn) => {
                        if (!descriptor.isFilterOnlyColumn && (userPreferenceColumnIds.includes(descriptor.columnId) || descriptor.isDefault === true)) {
                            return { ...descriptor, active: true };
                        }
                        else {
                            return { ...descriptor, active: false };
                        }
                    });
                    const doesNotHavePreferences = newTableDescriptor.every((item) => item.active === false);
                    // If saved column IDs no longer match the current descriptor (e.g. columnId changes),
                    // keep current defaults instead of hiding the entire table.
                    setTableDescriptor(doesNotHavePreferences ? tableDescriptor : newTableDescriptor);
                }
            }
            // legend filter addition for all selected initially
            if (legendKey) {
                const legendFilter = createLegendFilters();
                filters.push(legendFilter);
            }

            // handle static filters
            if (staticFilterKeys) {
                for (const key of staticFilterKeys) {
                    const filter = filters.find((filter) => filter.key === key);

                    // mark static filters as static
                    if (filter) {
                        filter.isStatic = true;
                        if (!filter.clearPolicy) {
                            filter.clearPolicy = "restore-default";
                        }
                    }
                    else {
                        // if the filter is not already in the list, add it
                        const originalCustomFilter = getOriginalStaticFilter && await getOriginalStaticFilter(key);
                        if (originalCustomFilter) {
                            filters.push(originalCustomFilter);
                        }
                    }
                }

                // Sort filters so static/pre-defined ones always appear in the declared order,
                // with any user-added non-static filters appended after.
                filters.sort((a, b) => {
                    const ai = staticFilterKeys.indexOf(a.key);
                    const bi = staticFilterKeys.indexOf(b.key);
                    if (ai === -1 && bi === -1) return 0;
                    if (ai === -1) return 1;
                    if (bi === -1) return -1;
                    return ai - bi;
                });
            }
            setFilters([...filters]);
            query.filters = filters;
            refreshData(query);
        } catch (error) {
            toast.warn(translate.byKey(TranslationKeys.loadTopicFailed))
        }
    };

    // pull user preferences for filters and columns on startup
    useEffect(() => {
        // fancy callback injection
        initialiser(setDetails);
        if (manualDataRefresh) {
            manualDataRefresh((newDataMethod?: (query?: ListQuery) => Promise<ListData>) => {
                refreshData(query, newDataMethod);
            });
        }
    }, []);

    useEffect(() => {
        if (tableDataUpdate) {
            tableDataUpdate((updateTableMethod: (tableData: any[]) => any[]) => {
                const rows = updateTableMethod(tableData);
                // only update the table data if the data has changed
                setTableData(prev => prev === rows ? prev : [...rows]);
            })
        }
    }, [tableData])


    // --> start sort
    const updateDirection = (columnId) => {
        const column = tableDescriptor.find(column => column.id === columnId);
        if (column) {
            const newDirection = column.sortDirection === Direction.ascending ? Direction.descending : Direction.ascending;
            const newSort: SortBy = { columnId: column.id, direction: newDirection, columnName: column.sortByName };
            query.sortBy = newSort;
            setTableDescriptor(tableDescriptor.map(col => col.id === columnId ? { ...col, sortDirection: newDirection } : col));
            refreshData(query)
        }
    }
    // <-- end sort

    // Helper to map saved filter values to their lookup option labels
    const mapSavedFilterValuesToLookupLabels = (columnDescriptor: TableColumn, savedSelected: any[], lookupArray: Lookup[]): any[] => {
        if (!columnDescriptor.lookupName) {
            return savedSelected;
        }

        const lookupEntry = lookupArray.find(l => l.name === columnDescriptor.lookupName);
        if (!lookupEntry || !lookupEntry.values || lookupEntry.values.length === 0) {
            return savedSelected;
        }

        return savedSelected.map(savedItem => {
            const matchingOption = lookupEntry.values.find(opt => opt.value === savedItem.value);
            return matchingOption ? { ...savedItem, label: matchingOption.label } : savedItem;
        });
    }

    // --> start legend
    const createLegendFilters = () => {
        const newFilter: Filter = {
            key: legendKey ?? '',
            label: legendLabel ?? '',
            showFilterChips: false,
            options: legendItems.map(legend => {
                return { value: `${legend.value}`, label: legend.label }
            }),
            selected: legendItems.map(legend => {
                return { value: `${legend.value}`, label: legend.label }
            })
        }
        return newFilter;
    }

    const onLegendFilterChange = (selectedLegend: TableLegendItem) => {
        const legend: TableLegendItem | undefined = legendItems.find(legend => legend.value === selectedLegend.value);
        const legendFilter = filters.find(filter => filter.key === legendKey);

        // if the legend or legend filter is not found, return
        if (!legend || !legendFilter) {
            return;
        }

        const selectedFilter = legendFilter.selected.find(option => option.value === legend.value);
        if (selectedFilter) {
            removeFilter(legendFilter, selectedFilter, undefined);
            legend.isSelected = false;
        }
        else {
            updateExistingFilter(legend.value, { value: legend.value, label: legend.label }, legendFilter, undefined);
            legend.isSelected = true;
        }

        const countSelected = legendItems.filter(item => item.isSelected).length
        if (countSelected === 1) {
            const onlySelected = legendItems.find(item => item.isSelected);
            if (onlySelected) {
                onlySelected.disabled = true;
            }
        }
        else {
            legendItems.forEach(item => {
                item.disabled = false;
            });
        }
    }
    // <-- end legend

    // --> start search
    const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const updateSearchTerm = (newSearchTerm) => {
        searchTerm.value = newSearchTerm;
        query.goToPage = 1;
        query.searchTerm = searchTerm;

        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
        }
        searchDebounceRef.current = setTimeout(() => {
            refreshData(query);
        }, 500);
    }
    const updateSearchColumn = (newSearchColumn) => {
        searchTerm.accessor = newSearchColumn;

        query.searchTerm = searchTerm;
        if (searchTerm.value) {
            refreshData(query);
        }
    }
    // <-- end search

    // --> start filters
    // IMPORTANT: all refreshData calls inside filter functions must pass silent=true.
    // Omitting silent causes ActivityFrame to replace the table with a loading spinner,
    // which unmounts TableHead/TableCell and resets the header filter popup to closed.
    // The above-table filter chips are unaffected because they render outside ActivityFrame.
    const [userFilterPreferences, setUserFilterPreferences] = useState<FilterPreference[]>(new Array(0));

    const updateNonColumnFilter = async (newSelectedOption: MultiSelectOption, filterKey: string, filterLabel: string, isSingleSelect: boolean, options: MultiSelectOption[] = []): Promise<void> => {
        // Check we already have this filter applied
        const filter = filters.find(filter => filter.key === filterKey);

        // Update the filter if it exists
        if (filter) {
            const selectedFilter = filter.selected.find((selectedFilter) => selectedFilter.value === newSelectedOption.value);

            // The option is ready applied, so remove it
            if (selectedFilter && !isSingleSelect) {
                // Remove the selected filter
                filter.selected.splice(filter.selected.indexOf(selectedFilter), 1);

                //Close the filter if there are no selected options
                if (filter.selected.length < 1) {
                    await closeFilter(filter.key)
                }
                return;
            }

            // Update the filter with the new selected option
            const index = filters.indexOf(filter);
            const selected = isSingleSelect ? [] : filter.selected;
            filters[index] = { ...filter, selected: [...selected, newSelectedOption] };
            setFilters([...filters]);
            query.filters = filters;
            await refreshData(query, undefined, true);

        }
        // Add the filter if it doesn't exist
        else {
            const newFilter: Filter = {
                key: filterKey,
                label: filterLabel,
                options: options,
                selected: [newSelectedOption]
            }

            filters.push(newFilter);
            setFilters([...filters]);
            query.goToPage = 1;
            query.filters = filters;
            await refreshData(query, undefined, true);
        }
    }

    const updateFilters = async (columnId, itemId, itemValue, columnKey, elementSysname): Promise<void> => {
        const filterColumn: TableColumn | undefined = newTableDescriptor.find((column: TableColumn) => {
            return columnKey ? column.searchOnName === columnKey : column.id === columnId
        })
            || newTableDescriptor.find((column: TableColumn) => column.filterKey === columnKey)
            || newTableDescriptor.find((column: TableColumn) => column.accessor === columnKey);

        if (!filterColumn?.searchOnName) {
            console.warn("[TableContextProvider] Unable to resolve filter column mapping", {
                columnId,
                columnKey,
                itemId,
                itemValue,
            });
            return;
        }

        let newSelectedFilter: MultiSelectOption = { value: itemId, label: itemValue, elementSysname };
        const filter = filters.find(filter => { return filter.key === filterColumn.searchOnName; })
        if (filter) {
            // Wait while we do clean for single select filter
            if (lookup.find(lookup => lookup.name === filterColumn.lookupName)?.isSingleSelect) {
                await handleSingleSelectFilter(filterColumn.filterId ?? "");
                filter.selected = [];
            }

            const selectedFilter = filter.selected.find((selectedFilter) => selectedFilter.value === itemId);

            if (selectedFilter) {
                await removeFilter(filter, selectedFilter, filterColumn)
            }
            else {
                //This is an existing filter so we need the setting data id to update, because the filter already exist.
                newSelectedFilter = {
                    ...newSelectedFilter,
                    settingDataId: filterColumn.filterId === FILTER_RESULT
                        ? undefined
                        : (filter.selected.length > 0 ? filter.selected[0].settingDataId : undefined)
                };

                //If this filter has filter that we need to ignore when building the filter, we need to remove them.
                filter.selected = filter.selected.filter(selected => selected.ignore !== true);

                await updateExistingFilter(itemId, newSelectedFilter, filter, filterColumn)
            }
        }
        else {
            const newFilter: Filter = {
                key: filterColumn.searchOnName as string,
                label: translate.byKey(TranslationKeys[filterColumn.filterTranslationKey ?? filterColumn.translationKey]) ?? filterColumn.heading,
                options: new Array(0),
                selected: [
                    newSelectedFilter
                ]
            }
            await saveFilterPreference(filterColumn, newFilter, false);
        }
    }

    const handleMenuSelection = async (cols) => {
        try {
            let columnsToBeAdded = [] as any
            let columnsToBeRemoved = [] as any

            cols.map(col => {
                // Not all columns have a settingTypeId yet. We may also not have the userPreference.userPreferenceColumnIds
                let supportsPersist = !!col.columnId && !!userPreference && !!userPreference.userPreferenceColumnIds;

                if (supportsPersist) {
                    if (!(userPreference.userPreferenceColumnIds.includes(col.columnId)) && col.active) {
                        columnsToBeAdded.push(col)
                        userPreference.userPreferenceColumnIds.push(col.columnId);
                    } else if (userPreference.userPreferenceColumnIds.includes(col.columnId) && !col.active) {
                        columnsToBeRemoved.push(col)

                        // remove column id after deleting to avoid deleting non existing data next time you delete a column.
                        const index = userPreference.userPreferenceColumnIds.indexOf(col.columnId);
                        if (index > -1) {
                            userPreference.userPreferenceColumnIds.splice(index, 1);
                        }
                    }
                }
            })

            if (columnsToBeAdded.length > 0) {
                for (const item of columnsToBeAdded) {
                    const columnPreference = {
                        menuSettingTypeId: userPreference.currentMenuDetail,
                        columnId: item.columnId,
                        userLoginId: userPreference.userLoginId,
                        reportId: userPreferenceKey?.reportId,
                        settingId: userPreferenceKey?.settingId
                    }
                    await storeUserPreferenceColumn(columnPreference);
                }
            }

            if (columnsToBeRemoved.length > 0) {

                // select only those ids that exist in columnsToBeRemoved
                const idsToRemove = columnsToBeRemoved.map((column) => {
                    return userPreference?.userPreferenceColumms?.find(item => item.columnId === column.columnId).id
                })

                for (const id of idsToRemove) {
                    await deleteUserPreference(id);
                }
            }
        } catch (error) {
            showError(error, toast.warn);
        }
    }

    const closeFilter = async (accessor: string) => {
        const filterBeingRemoved = filters.find((filter) => filter.key === accessor)
        if (isClearLocked(filterBeingRemoved)) {
            return;
        }

        let newFilters = filters.filter((filter) => filter.key !== accessor)
        const columnAssociatedWithFilter = tableDescriptor.find(column => column.searchOnName === accessor)
        const preferenceToDelete = userFilterPreferences.find(filterPreference => `${filterPreference.filterId}` === `${columnAssociatedWithFilter?.filterId}`)

        if (preferenceToDelete) {
            try {
                await deleteUserPreference(preferenceToDelete.preferenceId);
                const newFilterPreferences = userFilterPreferences.filter(filterPreference => filterPreference.preferenceId !== preferenceToDelete.preferenceId);
                setUserFilterPreferences(newFilterPreferences)
            } catch (error) {
                showError(error, toast.warn);
            }
        }

        //If the filter is a static filter, we need to add it back to the filter list with the default selected option.
        if (shouldRestoreFilterOnClear(filterBeingRemoved)) {
            const originalCustomFilter = getOriginalStaticFilter && await getOriginalStaticFilter(filterBeingRemoved.key);
            if (originalCustomFilter) {
                const index = filters.findIndex((filter) => filter.key === filterBeingRemoved.key);
                newFilters.splice(index, 0, originalCustomFilter);
            } else {
                newFilters.push({
                    key: filterBeingRemoved.key,
                    label: filterBeingRemoved.label,
                    options: [],
                    selected: [
                        {
                            label: translate.byKey(TranslationKeys.all),
                            value: '*',
                            ignore: true
                        }
                    ],
                    isCustomFilter: true,
                    isStatic: true,
                    clearPolicy: "restore-default"
                });
            }
        }

        query.filters = newFilters;
        setFilters([...newFilters])
        // When loadDataOnDemand is true, don't auto-run on filter close. User must click "Run Report".
        // This prevents concurrent save/delete/save SQL errors and slow responses on large systems.
        if (!loadDataOnDemand) {
            await refreshData(query, undefined, true);
        } else {
            showFilterChangeNotification();
        }
    }

    const buildSettingDataForUpdateFilter = (column: TableColumn, newSelectedFilter: MultiSelectOption, itemId: string, filter: Filter, preferenceId: string) => {

        if (column.filterId === FILTER_DATE || column.filterId === FILTER_TIME || column.filterId === FILTER_INACTIVE_FOR || column.filterId === FILTER_TIMEOUT_MINUTES || column.filterId === FILTER_RESULT) {
            const existingSettingDataId = filter.selected?.[0]?.settingDataId ?? newSelectedFilter.settingDataId;
            const settingDataId = existingSettingDataId ? parseInt(existingSettingDataId) : 0;

            return {
                settingDataId,
                value: newSelectedFilter.value,
                userPreference: preferenceId,
                filterSettingTypeId: column.filterId === FILTER_RESULT ? undefined : column.filterId,
                eSysnameLookup: newSelectedFilter.elementSysname,
                dynamicObject: {
                    report: {
                        data: {
                            type: 'report',
                            id: userPreferenceKey?.reportId
                        }
                    }
                }
            };
        }

        // Some filters persist only the selected value and do not map to a relationship entity.
        if (!column?.settingDataName) {
            return {
                value: itemId,
                userPreference: preferenceId,
                filterSettingTypeId: column?.filterId,
                dynamicObject: {}
            }
        }

        let newDynamicObject = {};
        newDynamicObject[column?.settingDataName] = {
            data: {
                type: column?.settingDataName,
                id: newSelectedFilter.value
            }
        }

        return {
            value: itemId,
            userPreference: preferenceId,
            filterSettingTypeId: column?.filterId,
            dynamicObject: { ...newDynamicObject }
        }
    }

    const updateExistingFilter = async (itemId: string, newSelectedFilter: MultiSelectOption, filter: Filter, column?: TableColumn) => {
        try {
            const linkToPreference = userFilterPreferences.find((userFilterPreference) => `${userFilterPreference.filterId}` === `${column?.filterId}`)
            if (linkToPreference && column?.filterId) {
                const settingData = buildSettingDataForUpdateFilter(column, newSelectedFilter, itemId, filter, linkToPreference?.preferenceId);
                await storeSettingData(settingData);
            } else if (!!!linkToPreference && column?.filterId && filter.isCustomFilter) {
                /**
                * Custom filter are not persisted until a user decide to.
                * That is why they need to be treated as new preferences since they are not persisted
                * saveFilterPreference will handle the refresh of data so once complete terminate.
                */

                //Remove old options with new options. This make new filter
                filter.selected.splice(0, filter.selected.length, newSelectedFilter);

                //Check if it qualify to be a preference if so, change custom filter flag to false.
                if (userPreferenceKey && column && column.filterId) {
                    filter.isCustomFilter = false;
                }

                saveFilterPreference(column, filter, true);
                return;
            }

            if (column?.filterId === FILTER_DATE) {
                filter.selected = processSelectedDates([newSelectedFilter], translate);
            }
            else if (column?.filterId === FILTER_TIME || column?.filterId === FILTER_INACTIVE_FOR || column?.filterId === FILTER_TIMEOUT_MINUTES) {
                filter.selected = [newSelectedFilter];
            }
            else {
                filter.selected.push(newSelectedFilter);
            }

            const newFilters = [...filters]
            setFilters(newFilters);
            query.goToPage = 1;
            query.filters = newFilters;
            // When loadDataOnDemand is true, don't auto-run on filter update. User must click "Run Report".
            // This prevents concurrent save/delete/save SQL errors and slow responses on large systems.
            if (!loadDataOnDemand) {
                refreshData(query, undefined, true);
            } else {
                showFilterChangeNotification();
            }

            //After successful update, if this filter is a single select and have a dependencies, we need to close the dependencies when updating this filter.
            const filterLookup = lookup.find(lookup => lookup.name === column?.lookupName);

            if (filterLookup && filterLookup.isSingleSelect) {
                const dependencies = lookup.filter(lookupItem => lookupItem.filterDependentKey === filter.key);

                dependencies.forEach(async (dependentLookup) => {
                    const filter = filters.find(filter => filter.key.startsWith(dependentLookup.name));
                    if (filter && filter.selected.length > 0) {
                        filter && await closeFilter(filter?.key);
                    }
                })
            }
        } catch (error) {
            showError(error, toast.warn);
        }
    }

    const saveFilterPreference = async (column: TableColumn, filter: Filter, isCustomFilter: boolean) => {
        try {
            const filterPreference = userFilterPreferences.find((userFilterPreference) => `${userFilterPreference.filterId}` === `${column?.filterId}`);
            let newPreference: any;

            // If the filter preference does not exist, create a new one.
            if (!!!filterPreference && userPreferenceKey && column && column.filterId) {
                const userPreferenceFilter: UserPreferenceFilter = {
                    filterId: column.filterId,
                    userLoginId: userPreferenceKey?.userLoginId,
                    currentMenuDetail: userPreferenceKey?.currentMenuDetailId,
                    filterData: filter.selected.map(selectedValue => {
                        return {
                            filterTypeName: column.settingDataName,
                            value: selectedValue.value,
                            elementSysname: selectedValue.elementSysname
                        };
                    }),
                    reportId: userPreferenceKey?.reportId,
                    settingId: userPreferenceKey?.settingId
                };

                newPreference = await storeUserPreferenceFilters(userPreferenceFilter);
                userFilterPreferences.push({ filterId: newPreference.filterId, preferenceId: newPreference.id });
            }
            // If the preference already exist only add the setting data
            else if (filterPreference && column && column.filterId) {
                filter.selected.forEach(async (selectedFilter) => {
                    const settingData = buildSettingDataForUpdateFilter(column, selectedFilter, selectedFilter.value, filter, filterPreference.preferenceId);
                    await storeSettingData(settingData);
                })
            }

            // If the preference was created update the filter to include settingDataId
            if (column.filterId && newPreference) {
                if (column.filterId === FILTER_DATE) {
                    filter.selected = processSelectedDates(filter.selected, translate);
                }

                filter.selected[0].settingDataId = newPreference.settingDataId;
            }

            // Custom filters will already exist in the filter list, so we need to update them.
            if (isCustomFilter) {
                const index = filters.findIndex((existingfilter: Filter) => existingfilter.key === filter.key);
                filters[index] = filter;
                setFilters([...filters]);
            }
            else {
                filters.push(filter);
                setFilters([...filters]);
            }

            //We do this either way because we have some view that don't support filter preference and we still need to apply the filter.
            query.goToPage = 1;
            query.filters = filters;
            // When loadDataOnDemand is true, don't auto-run on filter save. User must click "Run Report".
            if (!loadDataOnDemand) {
                refreshData(query, undefined, true);
            } else {
                showFilterChangeNotification();
            }
        } catch (error) {
            showError(error, toast.warn);
        }
    }

    const removeFilter = async (filter: Filter, selectedFilter: MultiSelectOption, column?: TableColumn) => {
        try {
            const linkToPreference = userFilterPreferences.find((userFilterPreference) => `${userFilterPreference.filterId}` === `${column?.filterId}`);

            if (linkToPreference) {
                const settingDatas = await getSettingDataForUserLogin(linkToPreference.preferenceId, linkToPreference.filterId);
                const settingDataToBeDeleted = settingDatas.find(settingData => settingData.label === selectedFilter.value);

                if (settingDataToBeDeleted) {
                    await deleteSettingData(settingDataToBeDeleted.value)
                }
            }
            const index = filters.indexOf(filter);
            filter.selected.splice(filter.selected.indexOf(selectedFilter), 1);

            if (filter.selected.length < 1) {
                await closeFilter(filter.key)
            } else {
                filters[index] = filter;
                const newFilters = [...filters]
                setFilters(newFilters);
                query.filters = newFilters;
                // When loadDataOnDemand is true, don't auto-run on filter remove. User must click "Run Report".
                if (!loadDataOnDemand) {
                    refreshData(query, undefined, true);
                } else {
                    showFilterChangeNotification();
                }
            }
        }
        catch (error) {
            showError(error, toast.warn);
        }
    }

    /**
     * Check if the filter is a single select filter and delete the setting data associated with it.
     * @param filterId - The id of the filter that is a single select filter
     */
    const handleSingleSelectFilter = async (filterId: string) => {
        try {
            const linkToPreference = userFilterPreferences.find((userFilterPreference) => `${userFilterPreference.filterId}` === `${filterId}`)
            if (linkToPreference) {
                const settingDatas = await getSettingDataForUserLogin(linkToPreference?.preferenceId, linkToPreference?.filterId)
                for (const settingData of settingDatas) {
                    await deleteSettingData(settingData.value);
                }
            }
        } catch (error) {
            showError(error, toast.warn);
        }
    }

    // <-- end filters

    // --> start pagination
    const updatePageNumber = (pageNumber: number) => {
        if (pageNumber > 0 && pageNumber <= pagination.totalPages) {
            query.goToPage = pageNumber;
            refreshData(query);
        }
    }

    const updatePageSize = (pageSize: number) => {
        if (pagination.sizes?.includes(pageSize)) {
            query.goToPage = 1;
            query.pageSize = pageSize;
            refreshData(query);
        }
    }
    // <-- end pagination

    const updateTableDescriptor = (newTableDescriptor) => {
        // Re-inject filter-only columns (e.g. dateOnly, hoursWorked) that are excluded from
        // the column selector UI but are still needed for filter lookups in tableDescriptor.
        const filterOnlyColumns = tableDescriptor.filter(col => col.isFilterOnlyColumn);
        setTableDescriptor([...newTableDescriptor, ...filterOnlyColumns]);
    }

    const processData = (listData: ListData) => {
        setTableData(listData.rows);
        setGroupedTableData(listData.groupedRows ?? []);
        const newPages = Array.from(new Array(5), (_, index) => {
            // only slide for slidable page count
            if (index < listData.pagination.totalPages
                // slide upto the total pages but no further
                && index + listData.pagination.pageNumber - 2 <= listData.pagination.totalPages) {
                return listData.pagination.pageNumber < 4
                    // no sliding for pages 1, 2, 3
                    ? index + 1
                    // keep two pages on the left of selected page
                    : index + listData.pagination.pageNumber - 2;
            }

            // falls outside of slider
            else {
                return 0;
            }
        }).filter(page => page > 0);
        // override pagination to force re-render on the paginator
        setPagination({ ...pagination, ...listData.pagination, pages: newPages });
    }

    /**
     * Fetches the current table data using the supplied query.
     *
     * @param silent - When true, the busy/loading spinner is suppressed so that the table
     *   remains mounted throughout the fetch. This is important for filter add/remove operations:
     *   the table header filter popup (TableCell) stores its open state locally, so if the table
     *   unmounts (because ActivityFrame replaces children with a spinner), the popup closes and
     *   the user loses context. Pass silent=true for any call site that is triggered by a user
     *   interacting with a filter, so the popup stays open across the data refresh.
     */
    const refreshData = async (query: ListQuery, newDataMethod?, silent?: boolean) => {
        if (loadDataOnDemand && !requestToLoadDataOnDemand && !hasRunOnDemandRef.current) {
            if (loadDataToastRef.current === 0) {
                loadDataToastRef.current = toast.info(translate.byKey(TranslationKeys.clickRunReport), { autoClose: false, closeOnClick: false, bodyClassName: "font-normal" });
            }
            return;
        }

        if (!silent) setBusy(true)
        try {
            if (newDataMethod) {
                setDataMethod(() => newDataMethod);
                const data: ListData = await newDataMethod(query)
                processData(data);
                hasRunOnDemandRef.current = true;
                return;
            }

            const data = await dataMethod(query)
            processData(data);
            hasRunOnDemandRef.current = true;
        } catch (error) {
            showError(error, toast.warn);
        } finally {
            if (!silent) setBusy(false)
        }
    };

    const exportData = async (event: MultiSelectOption, title: string, description: string) => {
        setBusy(true);
        try {
            let page: number = 1;
            let dataRows = [];
            let dataModel;
            const maxRowsToDownload = 10000; // This is a safeguard to prevent downloading too much data. Adjust as necessary.

            do {
                if (!exportRef.current) {
                    return;
                }

                if (newDataMethod)
                    dataModel = await newDataMethod({
                        goToPage: page,
                        pageSize: 100,
                        sortBy: sort,
                        filters: filters,
                        searchTerm,
                    });
                dataRows = dataRows.concat(dataModel.rows);
                page++;
            } while (page <= dataModel.pagination.totalPages && dataRows.length < maxRowsToDownload);

            if (!exportRef.current) {
                return;
            }

            let report: ExportableReport = {
                title: `${title}`,
                columns: tableDescriptor.filter((column) => column.active),
                childColumns: childColumns,
                rows: dataRows,
                description: translate.byKeyFormatted(TranslationKeys.reportSubTitle, { description }),
                user: user.name,
                filename: translate.byKeyFormatted(TranslationKeys.reportFilename, { title }),
                filters: filters,
                date: getDateWithFullTime(),
                searchTerm: getSearchTermAndColumnHeading(searchTerm),
                menuId: userPreferenceKey?.currentMenuDetailId,
                reportId: userPreferenceKey?.reportId
            };

            exportReport(report, event.value, translate, dialog);
        } catch (error) {
            if (exportRef.current) {
                toast.warn(translate.byKey(TranslationKeys.failedToGenerateReport));
            }
        } finally {
            if (exportRef.current) {
                setBusy(false);
            }
        }
    };

    // --> start grouped export
    const [collapsedGroups, setCollapsedGroupsCtx] = useState<Record<string, boolean>>({});
    const [reportTitle, setReportTitle] = useState<string>('');

    const syncCollapsedGroups = (groups: Record<string, boolean>) => {
        setCollapsedGroupsCtx(groups);
    };

    const downloadGroup = async (parentRow: any, childRows: any[], format: string) => {
        setBusy(true);
        try {
            const firstActiveCol = tableDescriptor.find(c => c.active);
            const rawGroupLabel = firstActiveCol ? (parentRow._raw ?? parentRow)[firstActiveCol.accessor] : undefined;
            const groupLabel = (rawGroupLabel != null && typeof rawGroupLabel !== 'object') ? String(rawGroupLabel) : '';
            const titleLabel = reportTitle
                ? (groupLabel ? `${reportTitle} - ${groupLabel}` : reportTitle)
                : (groupLabel || translate.byKey(TranslationKeys.report) || 'Export');
            const activeColumns = tableDescriptor.filter(c => c.active);
            const report: ExportableReport = {
                title: titleLabel,
                columns: activeColumns,
                childColumns: childColumns,
                rows: [parentRow, ...childRows],
                description: '',
                user: user.name,
                filename: translate.byKeyFormatted(TranslationKeys.reportFilename, { title: titleLabel }),
                filters: [],
                date: getDateWithFullTime(),
                searchTerm: getSearchTermAndColumnHeading(searchTerm),
            };
            exportReport(report, format, translate, dialog);
        } catch (error) {
            toast.warn(translate.byKey(TranslationKeys.failedToGenerateReport));
        } finally {
            setBusy(false);
        }
    };

    const exportVisibleData = async (format: string) => {
        setBusy(true);
        try {
            const hasGroupedData = groupedTableData && groupedTableData.length > 0;
            const rows: any[] = [];

            if (hasGroupedData) {
                (groupedTableData ?? []).forEach(group => {
                    rows.push(group.parentRow);
                    const isExpanded = !collapsedGroups[`${group.id}`];
                    if (isExpanded) {
                        rows.push(...group.childRows);
                    }
                });
            } else {
                rows.push(...tableData);
            }

            const title = reportTitle || translate.byKey(TranslationKeys.report) || 'Export';
            const report: ExportableReport = {
                title,
                columns: tableDescriptor.filter(c => c.active),
                childColumns: hasGroupedData ? childColumns : undefined,
                rows,
                description: '',
                user: user.name,
                filename: translate.byKeyFormatted(TranslationKeys.reportFilename, { title }),
                filters: filters,
                date: getDateWithFullTime(),
                searchTerm: getSearchTermAndColumnHeading(searchTerm),
            };
            exportReport(report, format, translate, dialog);
        } catch (error) {
            toast.warn(translate.byKey(TranslationKeys.failedToGenerateReport));
        } finally {
            setBusy(false);
        }
    };
    // <-- end grouped export

    const getSearchTermAndColumnHeading = (newSearch: SearchTerm): SearchTerm => {
        let search: SearchTerm = { accessor: newSearch.accessor, translationKey: "" };

        searchDescriptor.forEach((col) => {
            if (col.searchOnName === newSearch.accessor) {
                search.value = newSearch.value;
                search.heading = translate.byKey(TranslationKeys[col.translationKey]);
            }
        });

        return search;
    };

    const findItemName = (id: string, isCustomDeleteKey?: boolean) => {
        let item: any;

        if (isCustomDeleteKey) {
            item = tableData.find(row => row[customDeleteKey?.idKey!] && row[customDeleteKey?.idKey!].toString() === id);
        } else {
            item = tableData.find(row => row.id.toString() === id);
        }

        return customDeleteKey?.nameKey ? item[customDeleteKey?.nameKey] : item.name;
    }

    const onDelete = (id: string, isCustomDeleteKey?: boolean) => {
        if (id && deleteMethod) {
            deleteMethod(id, isCustomDeleteKey)
                .then(() => {
                    toast.success(translate.byKey(TranslationKeys.deleteSuccess))
                })
                .catch((error) => {
                    dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) })
                });
        }
    }

    const handleDelete = (id: string, isCustomDeleteKey?: boolean) => {
        let deleteMessage: string = translate.byKey(TranslationKeys.permanentlyDeleteRecord);
        
        if (customDeleteMsgTrnKey) {
            deleteMessage = translate.byKey(customDeleteMsgTrnKey)
        } 
        else if (customDeleteMsgTrnKeyWithCondition) {
            const itemToBeDeleted = tableData.find(item => item.id === id);
            const rowValue = itemToBeDeleted && itemToBeDeleted[customDeleteMsgTrnKeyWithCondition.fieldName];
            const valueToCheck = customDeleteMsgTrnKeyWithCondition.value
            deleteMessage = performCheck(rowValue, valueToCheck, customDeleteMsgTrnKeyWithCondition.operator) ? translate.byKey(customDeleteMsgTrnKeyWithCondition.translationKey) : deleteMessage;
        }

        const dialogContent: DialogContent = {
            title: translate.byKeyFormatted(TranslationKeys.deleteRecordWithName, [findItemName(id, isCustomDeleteKey)]),
            message: deleteMessage,
            buttons: [
                {
                    id: "btn_confirmDelete",
                    onClick: () => { onDelete(id, isCustomDeleteKey) },
                    text: translate.byKey(TranslationKeys.confirm),
                    type: ButtonTypes.RedRoundedButton
                },
                {
                    id: "btn_Cancel",
                    onClick: () => { }, // Empty onClick will just close the dialog
                    text: translate.byKey(TranslationKeys.cancel),
                    type: ButtonTypes.TextButton,
                    className: "justify-center px-4 py-1 text-base font-medium sm:ml-3 sm:w-auto sm:text-sm"
                }
            ]
        }

        dialog.pushInfo(dialogContent);
    }

    const deleteItem = () => {
        if (deleteMethod === undefined) {
            return undefined;
        }

        return handleDelete;
    }

    const onShowWarning = (warningMessage: CellWarning) => {
        if (warningMessage) {
            dialog.pushInfo({
                title: warningMessage.title ?? translate.byKey(TranslationKeys.warning),
                message: warningMessage.message,
            });
        }
    }
    
    return <TableContext.Provider value={
        {
            isInUse: useTable,
            isMultiSelect,
            tableDescriptor,
            tableData,
            groupedTableData,
            updateTableDescriptor,
            deleteItem: deleteItem(),
            exportData,
            editItem: editMethod,
            swapItem: swapMethod,
            customDeleteKey,
            alwaysSelectedAndDisabled,
            downloadItem: downloadMethod,
            busy: busy || externalBusy,
            customDelete,
            customDeleteWithRange,
            customEdit,
            customEditWithRange,
            customDownload,
            canExportReport,
            manuallyMuster,
            customMusteringWithRange,
            menuId: userPreferenceKey?.currentMenuDetailId,
            onShowWarning,
            onMoveUpOrDown,
            loadDataOnDemand,
            setRequestToLoadDataOnDemand,
            emptyStateMessage,
            onAddNew,
            showAddNewButton,
            runDataOnDemandButtonLabel,
            rowTooltipConfig,
            childColumns,
            downloadGroup: childColumns.length > 0 ? downloadGroup : undefined,
            syncCollapsedGroups: childColumns.length > 0 ? syncCollapsedGroups : undefined,
            exportVisibleData: childColumns.length > 0 ? exportVisibleData : undefined,
            setReportTitle,
        }
    }>
        <FilterContext.Provider value={{ isInUse: useFilter, filters, updateFilters, closeFilter, updateNonColumnFilter, lookup }}>
            <SortContext.Provider value={{ isInUse: useSort, tableDescriptor, updateDirection }}>
                <PaginationContext.Provider value={{ isInUse: usePagination, pagination, updatePageNumber, updatePageSize }}>
                    <LegendContext.Provider value={{ isInUse: useLegend, legendItems, onLegendFilterChange, tableLegendSelectable: legendKey ? true : false }}>
                        <SearchContext.Provider value={{ isInUse: useSearch, searchDescriptor, searchTerm, updateSearchColumn, updateSearchTerm, disableSearch }}>
                            {children}
                        </SearchContext.Provider>
                    </LegendContext.Provider>
                </PaginationContext.Provider>
            </SortContext.Provider>
        </FilterContext.Provider>
    </TableContext.Provider>
};
